import React, { FC, useCallback, useState } from 'react';
import styled from 'styled-components';
import { AttachmentType, SimulatorResultItem } from '@sdir/httpclient/lib/clients/aps/application';
import { FileUpload, Loader, SdirModal, SdiUpload, TextLink } from '@sdir/sds';
import { useIntl } from 'react-intl';
import { generatePath } from 'react-router-dom';
import { useCurrentApplication, useSubmitSequence } from '../../../services/hooks';
import {
  buildCreateAttachmentGroupSequence,
  CreateAttachmentGroupTracker,
} from '../../../helpers/attachmentApiHelpers';
import { editRouteForAttachmentType } from '../../../helpers/Qualifications';
import { SubmitSequenceSummary } from '../SubmitSequenceSummary';
import { useNavigateWithLang } from '../../../services/hooks/useNavigateWithLang';

type NewGroupActionProps = {
  type: AttachmentType;
  requirement?: SimulatorResultItem | null;
};

export const NewGroupAction: FC<NewGroupActionProps> = ({ requirement, type }) => {
  const navigate = useNavigateWithLang();
  const intl = useIntl();
  const { application, refresh } = useCurrentApplication();
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showSubmitResults, setShowSubmitResults] = useState(false);
  const [submitTracker, setSubmitTracker] = useState<CreateAttachmentGroupTracker>({});
  const { submit, isSubmitting, results } = useSubmitSequence(buildCreateAttachmentGroupSequence);
  const handleFilesSelected = useCallback(
    async (files: File[]) => {
      if (!files.length || !application.result) return;
      setUploadProgress(0);
      const { tracker } = await submit(
        {
          application: application.result,
          attachmentType: type,
          files,
          simulatorResultItemUri: requirement?.uri,
          progressListener: setUploadProgress,
        },
        submitTracker
      );
      if (tracker.errors?.length) {
        setShowSubmitResults(true);
        setSubmitTracker(tracker);
      } else {
        const createdGroupUid =
          Object.values(tracker.addedAttachments || {})[0].result?.group || '';
        refresh?.();
        const route = editRouteForAttachmentType(type);
        navigate(
          generatePath(route, {
            applicationUid: application.result.uid || '',
            attachmentGroup: createdGroupUid || '',
          })
        );
      }
    },
    [application.result, navigate, refresh, requirement?.uri, submit, submitTracker, type]
  );

  const hideErrorModal = useCallback(() => setShowSubmitResults(false), []);

  return (
    <>
      <FileUpload
        allowedTypes=".pdf"
        buttonLabel=""
        multiple
        uploadProgress={isSubmitting ? uploadProgress : undefined}
        onFilesSelected={handleFilesSelected}
        renderButton={({ onClick }) => (
          <UploadLinkWrapper>
            <TextLink
              text={intl.formatMessage({ id: 'app.requirements.table.upload' })}
              click={onClick}
              icon={<SdiUpload size="s" height="1em" />}
            />
            {isSubmitting && <Loader size="16px" />}
          </UploadLinkWrapper>
        )}
      />
      {showSubmitResults && (
        <SdirModal
          visible
          cancelText={intl.formatMessage({ id: 'general.form.close' })}
          closeButton
          onCancel={hideErrorModal}
        >
          <SubmitSequenceSummary results={results} />
        </SdirModal>
      )}
    </>
  );
};

const UploadLinkWrapper = styled.div`
  display: flex;
  align-items: center;
  height: 100%;
  gap: 8px;
`;
